import { useMemo } from "react";

import { SearchSuggestionsField } from "types/generated";

import { FilterItem, FiltersDictionary, FiltersItemsOptionsMap } from "./types";
import {
  getFilteredSuggestions,
  makeFilterItemFromSuggestionField,
  makeFilterItemOptionsFromSuggestionField,
  mapDictionaryKey,
} from "./helpers";

type UseMakeFiltersDataProps = {
  suggestions: SearchSuggestionsField[];
  dictionary: Record<string, string>;
  excludedFilterFields?: string[];
  excludedFilterValues?: Record<string, (string | number | boolean)[]>;
};

const makeCustomKey = (key: string, dictionary?: FiltersDictionary) => {
  if (key.startsWith("label")) {
    return key;
  }

  return mapDictionaryKey(key, dictionary);
};

const useMakeFiltersData = ({
  suggestions,
  dictionary,
  excludedFilterFields,
  excludedFilterValues,
}: UseMakeFiltersDataProps) => {
  const [filters, filterOptionsMap] = useMemo<[FilterItem[], FiltersItemsOptionsMap]>(() => {
    const filtersItemsOptionsMap: FiltersItemsOptionsMap = new Map([]);

    const filtered = getFilteredSuggestions(suggestions, excludedFilterFields);

    return [
      filtered.map((field) => {
        const options = makeFilterItemOptionsFromSuggestionField(field);

        if (options) {
          const filteredOptions = excludedFilterValues?.[field.name]
            ? options.filter(({ value }) => !excludedFilterValues?.[field.name]?.includes(value))
            : options;

          filtersItemsOptionsMap.set(field.name, filteredOptions);
        }

        return makeFilterItemFromSuggestionField({ field, dictionary, makeCustomKey });
      }) || [],
      filtersItemsOptionsMap,
    ];
  }, [suggestions, dictionary, excludedFilterFields, excludedFilterValues]);

  return {
    filters,
    filterOptionsMap,
  };
};

export default useMakeFiltersData;
